import {
  SelectProps as AriaSelectProps,
  Button,
  ListBox,
  ListBoxItemProps,
  SelectValue,
  ValidationResult,
} from "react-aria-components";
import { cn, composeTailwindRenderProps } from "../../cn";
import Icon from "../../foundations/Icons/Icon";
import { inputStyle } from "../Form/commonStyles";
import { Description, FieldError, Label } from "../Form/Form";
import { DropdownItem, DropdownSection } from "../ListBox/ListBox";
import { DropdownSectionProps } from "../ListBox/types";
import SelectPopover from "./SelectPopover";
import SelectTrigger from "./SelectTrigger";

export interface SelectProps<T extends object>
  extends Omit<AriaSelectProps<T>, "children"> {
  label?: React.ReactElement | string;
  description?: string;
  errorMessage?: string | ((validation: ValidationResult) => string);
  items?: Iterable<T>;
  popoverClassName?: string;
  size?: "sm" | "l";
  children: React.ReactNode | ((item: T) => React.ReactNode);
}

export default function Select<T extends object>({
  label,
  description,
  errorMessage,
  children,
  items,
  popoverClassName,
  size = "l",
  ...props
}: SelectProps<T>) {
  return (
    <SelectTrigger
      {...props}
      className={composeTailwindRenderProps(
        props.className,
        "group flex flex-col gap-1",
      )}
    >
      {label && <Label>{label}</Label>}
      <Button className={(styles) => inputStyle({ ...styles, size })}>
        <SelectValue className="flex-1 text-left" />
        <Icon
          icon="arrow_small_bottom"
          aria-hidden
          className={cn(size === "l" ? "size-4 ml-2" : "size-2 ml-1")}
        />
      </Button>
      {description && <Description>{description}</Description>}
      <FieldError>{errorMessage}</FieldError>
      <SelectPopover className="min-w-[--trigger-width]">
        <ListBox
          items={items}
          className={cn(
            "max-h-80 overflow-auto outline-hidden bg-white shadow-y-plus-3 rounded-4 p-2",
            popoverClassName,
          )}
        >
          {children}
        </ListBox>
      </SelectPopover>
    </SelectTrigger>
  );
}

export function SelectItem(props: ListBoxItemProps) {
  return <DropdownItem {...props} />;
}

export function SelectSection<T extends object>(
  props: DropdownSectionProps<T>,
) {
  return <DropdownSection {...props} />;
}
